import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  readConsent,
  saveConsent,
  acceptAll,
  rejectAll,
  DEFAULT_CONSENT,
} from "@/lib/consent";

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPrefs(stored);
    }

    const onOpen = () => {
      const cur = readConsent() || DEFAULT_CONSENT;
      setPrefs(cur);
      setVisible(true);
      setManaging(true);
    };
    window.addEventListener("argengeo:open-cookie-preferences", onOpen);
    return () => window.removeEventListener("argengeo:open-cookie-preferences", onOpen);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    setManaging(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
    setManaging(false);
  };
  const handleSave = () => {
    saveConsent(prefs);
    setVisible(false);
    setManaging(false);
  };

  if (!visible) return null;

  return (
    <div className="ag-cookie-root" role="dialog" aria-modal="false" aria-label={t("cookie.bannerTitle")}>
      <div className={`ag-cookie ${managing ? "is-managing" : ""}`}>
        {!managing ? (
          <div className="ag-cookie__banner">
            <div className="ag-cookie__text">
              <h3 className="ag-cookie__title">{t("cookie.bannerTitle")}</h3>
              <p className="ag-cookie__desc">{t("cookie.bannerText")}</p>
            </div>
            <div className="ag-cookie__actions">
              <button
                type="button"
                className="ag-btn ag-btn--ghost"
                onClick={() => setManaging(true)}
              >
                {t("cookie.managePreferences")}
              </button>
              <button type="button" className="ag-btn ag-btn--outline" onClick={handleRejectAll}>
                {t("cookie.rejectAll")}
              </button>
              <button type="button" className="ag-btn ag-btn--primary" onClick={handleAcceptAll}>
                {t("cookie.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div className="ag-cookie__prefs">
            <div className="ag-cookie__prefsHead">
              <h3 className="ag-cookie__title">{t("cookie.preferencesTitle")}</h3>
              <p className="ag-cookie__desc">{t("cookie.preferencesIntro")}</p>
            </div>
            <ul className="ag-cookie__categories">
              <CategoryRow
                title={t("cookie.necessaryTitle")}
                desc={t("cookie.necessaryDesc")}
                checked={true}
                disabled
                onChange={() => {}}
                lockedLabel={t("cookie.alwaysActive")}
              />
              <CategoryRow
                title={t("cookie.analyticsTitle")}
                desc={t("cookie.analyticsDesc")}
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <CategoryRow
                title={t("cookie.marketingTitle")}
                desc={t("cookie.marketingDesc")}
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
              <CategoryRow
                title={t("cookie.personalizationTitle")}
                desc={t("cookie.personalizationDesc")}
                checked={prefs.personalization}
                onChange={(v) => setPrefs((p) => ({ ...p, personalization: v }))}
              />
            </ul>
            <div className="ag-cookie__actions ag-cookie__actions--wrap">
              <button type="button" className="ag-btn ag-btn--outline" onClick={handleRejectAll}>
                {t("cookie.rejectAll")}
              </button>
              <button type="button" className="ag-btn ag-btn--ghost" onClick={handleAcceptAll}>
                {t("cookie.acceptAll")}
              </button>
              <button type="button" className="ag-btn ag-btn--primary" onClick={handleSave}>
                {t("cookie.savePreferences")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ title, desc, checked, onChange, disabled = false, lockedLabel }) {
  return (
    <li className="ag-cookie__cat">
      <div className="ag-cookie__catText">
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
      <div className="ag-cookie__catToggle">
        {disabled ? (
          <span className="ag-cookie__locked">{lockedLabel}</span>
        ) : (
          <label className="ag-switch">
            <input
              type="checkbox"
              checked={!!checked}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="ag-switch__track" aria-hidden="true">
              <span className="ag-switch__thumb" />
            </span>
          </label>
        )}
      </div>
    </li>
  );
}
